import { useEffect, useRef, useState } from "react";
import { animate, motion, useReducedMotion } from "framer-motion";
import { Compass, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { NEEDS, VERDICTS, SLIDERS, DEFAULT_NEED, findSolution, calcLoss, formatNum, formatRub } from "./situationFinder.math.js";
import { Surface } from "./Surface";

/* Подбор в одной карточке: ситуация, вердикт сразу под выбором (решение владельца 2026-10-27, чтобы на телефоне тап
   давал видимый отклик), ниже свои цифры. Деньги считаются только
   из двух чисел самого клиента (часы в неделю × свой рубль за час × 52), цены услуги здесь нет.
   Ползунки стартуют с примера, в форму часы и ставка уходят, только если их сдвинули.
   Логика и данные: situationFinder.math.js. Тексты: copy-podbor-hero (copywriter-ru). */

const EASE = [0.22, 1, 0.36, 1];

/* Цветовой код общий с hero: ИИ = светлый `ai`, порядок = синий `accent`, по ситуации = пунктир. */
const VERDICT_STYLE = {
  ai: "border-ai/70 bg-ai/10 text-ai shadow-[0_0_28px_rgba(205,230,255,0.25)]",
  order: "border-accent/60 bg-accent/10 text-accent-soft",
  maybe: "border-dashed border-ai/50 bg-transparent text-ai/90",
};
const VERDICT_ICON = { ai: Sparkle, order: CheckCircle, maybe: Compass };

// число плавно догоняет цель; без анимации на первом показе и при reduce-motion
const useCount = (target, reduce) => {
  const [v, setV] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    if (reduce) {
      from.current = target;
      setV(target);
      return undefined;
    }
    const c = animate(from.current, target, {
      duration: 0.45,
      ease: EASE,
      onUpdate: (x) => {
        from.current = x;
        setV(x);
      },
    });
    return () => c.stop();
  }, [target, reduce]);
  return v;
};

const Step = ({ children }) => <p className="text-sm text-soft">{children}</p>;

const Chip = ({ id, label, active, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onClick}
    data-testid={`sf-need-${id}`}
    className={`min-h-11 rounded-full border px-3 py-2 text-center text-sm leading-tight transition-[color,background-color,border-color,box-shadow,transform] duration-300 ease-smooth active:scale-[0.96] sm:px-4 sm:text-base ${
      active ? "border-accent bg-accent/15 text-white shadow-ring" : "glass-lite border-transparent text-muted hover:text-white"
    }`}
  >
    {label}
  </button>
);

const Slider = ({ label, value, cfg, touched, format, onChange, testId, describedBy }) => (
  <label className="block">
    <span className="flex items-baseline justify-between gap-3 text-sm text-muted">
      {label}
      <span className={`font-mono tabular-nums ${touched ? "text-white" : "text-soft"}`}>
        {format(value)}
        {!touched && <span className="ml-2 text-xs">пример</span>}
      </span>
    </span>
    <input
      type="range"
      min={cfg.min}
      max={cfg.max}
      step={cfg.step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-valuetext={format(value)}
      aria-describedby={describedBy}
      data-testid={testId}
      data-example={!touched}
      style={{ "--p": `${((value - cfg.min) / (cfg.max - cfg.min)) * 100}%` }}
      className="rng mt-1"
    />
  </label>
);

const Row = ({ label, children }) => (
  <div>
    <span className="text-sm text-soft">{label}</span>
    <p className="mt-1 text-base text-white">{children}</p>
  </div>
);

export const SituationFinder = ({ scrollTo, onPick }) => {
  const [need, setNeed] = useState(DEFAULT_NEED);
  const [hours, setHours] = useState(SLIDERS.hours.example);
  const [rate, setRate] = useState(SLIDERS.rate.example);
  const [touched, setTouched] = useState({ hours: false, rate: false });
  const reduce = useReducedMotion();
  const s = findSolution(need);
  const money = calcLoss(hours, rate);
  const hoursShown = useCount(money.hoursYear, reduce);
  const moneyShown = useCount(money.moneyYear, reduce);
  const Icon = VERDICT_ICON[s.verdict];

  const move = (key, set) => (v) => {
    set(v);
    setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  };

  return (
    <Surface className="p-6 sm:p-8" data-testid="situation-finder">
      <div data-testid="sf-card">
        <Step>Ситуация</Step>
        <div role="radiogroup" aria-label="Ситуация" className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {Object.entries(NEEDS).map(([id, n]) => (
            <Chip key={id} id={id} label={n.short} active={need === id} onClick={() => setNeed(id)} />
          ))}
        </div>
        <div className="mt-5" aria-live="polite">
          <span className="text-sm text-soft">Где утекает</span>
          <p className="mt-1 text-base text-muted">{s.loss}</p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 border-t border-white/10 pt-8 lg:grid-cols-2 lg:gap-14">
          <motion.div
            key={need}
            initial={{ opacity: 0, y: reduce ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0 : 0.4, ease: EASE }}
          >
            <Step>Вердикт</Step>
            <span
              data-testid="sf-verdict"
              data-verdict={s.verdict}
              className={`mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-display text-lg font-medium ${VERDICT_STYLE[s.verdict]}`}
            >
              <Icon size={20} aria-hidden="true" className="shrink-0" />
              {VERDICTS[s.verdict]}
            </span>
            <p className="mt-3 text-base text-muted">{s.why}</p>
            <div className="mt-6 flex flex-col gap-5">
              <Row label="Что сделаю">{s.solution}</Row>
              <Row label="Что это даст">{s.gain}</Row>
            </div>
          </motion.div>

          <div>
            <Step>Твои цифры</Step>
            <div className="mt-4 flex flex-col gap-6">
              <Slider
                label="Часов в неделю на это"
                value={hours}
                cfg={SLIDERS.hours}
                touched={touched.hours}
                format={(v) => `${v} ч`}
                onChange={move("hours", setHours)}
                testId="sf-hours"
                describedBy="sf-hint"
              />
              <Slider
                label="Стоимость часа, ₽"
                value={rate}
                cfg={SLIDERS.rate}
                touched={touched.rate}
                format={formatRub}
                onChange={move("rate", setRate)}
                testId="sf-rate"
                describedBy="sf-hint"
              />
            </div>
            <p id="sf-hint" className="mt-4 text-sm text-soft">
              {touched.hours && touched.rate ? "Считаю только по твоим двум числам." : "Значения для примера. Подвинь на свои, цифра пересчитается."}
            </p>
            <p className="mt-6 text-sm text-soft">Цена ручной работы</p>
            <p className="mt-1 font-display text-2xl font-medium tabular-nums text-accent-soft sm:text-3xl">
              <span aria-hidden="true">
                ≈ {formatNum(Math.round(hoursShown))} ч и {formatRub(Math.round(moneyShown))} в год
              </span>
              <span className="sr-only" aria-live="polite" data-testid="sf-money">
                ≈ {formatNum(money.hoursYear)} ч и {formatRub(money.moneyYear)} в год
              </span>
            </p>
          </div>
        </div>

        <div className="mt-8 border-t border-white/10 pt-8">
          <button
            onClick={() => {
              onPick?.(s.label, touched.hours ? String(hours) : "", touched.rate ? String(rate) : "");
              scrollTo("form");
            }}
            data-testid="sf-cta"
            className="btn w-full sm:w-auto"
          >
            Получить разбор по этой ситуации
          </button>
          <button
            type="button"
            onClick={() => scrollTo("form")}
            className="mt-4 block text-sm text-muted underline underline-offset-4 transition-colors duration-300 ease-smooth hover:text-white"
            data-testid="sf-fallback"
          >
            Не нашёл своё среди вариантов? Опиши процесс в разборе
          </button>
          <p className="mt-4 max-w-[680px] text-sm text-soft">
            Это прикидка по твоим словам. Точный ответ даёт письменный разбор: там я смотрю процесс целиком.
          </p>
        </div>
      </div>
    </Surface>
  );
};
